/**
 * Página Sistema ProPoints™ — Liga ProKids (edición regular)
 *
 * Misma estructura que ProPointsSummerEdition, con los niveles
 * 30 · 60 · 90 propios de la temporada regular.
 */

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getRegisterUrl } from '../utils/registroOrigen';
import './ProPointsSummerEdition.css';

const ProPoints = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

    // Cargar FontAwesome
    const link = document.createElement('link');
    link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  return (
    <div className="propoints-landing">
      <main>

        {/* HERO */}
        <section id="landing" style={{ backgroundImage: "url('/img/landing/magnific_readaptame-la-siguiente-i_2898159435.png')", backgroundSize: 'cover', backgroundPosition: 'center' }}>
          <h1 className="anim-slide-left delay-1">SISTEMA PROPOINTS™</h1>
          <h1 className="subtitle anim-slide-right delay-1">Evolución, Motivación y Reconocimiento Personalizado</h1>
          <p className="description anim-slide-up delay-2">En Liga ProKids creemos que cada niño merece <b>mejorar</b>, <b>divertirse</b> y <b>ser reconocido</b> por lo que aporta. Por eso hemos creado el sistema ProPoints™, una herramienta de <b>evolución personal</b> pensada para premiar el <b>esfuerzo</b>, el <b>compromiso</b> y el <b>impacto positivo</b> en cada jornada.</p>
        </section>

        {/* CÓMO FUNCIONA */}
        <section id="como-funciona">
          <h2>¿CÓMO FUNCIONA?</h2>
          <div className="row">
            <div className="col">
              <article>
                <p>Cada jugador acumula <b>ProPoints™</b> según su <b>participación</b>, <b>rendimiento</b> y los <b>logros individuales</b> que puede conseguir en cada partido.</p>
                <p>Puedes ver tus puntos y progresos en tu <b>perfil de jugador</b>.</p>
                <p>A medida que acumulas puntos, <b>subes de nivel</b> (<b>Bronce</b>, <b>Plata</b> y <b>Oro</b>) y <b>desbloqueas regalos</b>.</p>
                <p>Además, hay <b>premios muy importantes</b> para los jugadores que <b>más puntos acumulen</b> al final del torneo en cada categoría.</p>
              </article>
              <div className="card">
                <h4>ACCIONES GENERALES</h4>
                <p>(Se aplican a todos los jugadores que participan)</p>
                <div className="card-content">
                  <div className="card-small"><i className="fa-solid fa-futbol icon"></i><b>Asistencia</b></div>
                  <div className="card-small"><i className="fa-solid fa-trophy icon"></i><b>Victoria del equipo</b></div>
                  <div className="card-small"><i className="fa-solid fa-ranking-star icon"></i><b>Ser uno de los jugadores destacados del equipo</b></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TIPOS DE LOGROS */}
        <section id="logros">
          <h2>TIPOS DE LOGROS</h2>
          <div className="row">
            <div className="col">
              <article>
                <p>Cuando se consigue un logro, se <b>desbloquea la carta</b> en vuestro perfil (se hace visible) y sumáis puntos (<b>3 en logros de partido</b> y <b>4 en logros especiales</b>). Además, en los logros individuales de partido se recibe un <b>trofeo físico</b> que es una réplica de la carta y se fotografía al niño/a con el logro obtenido.</p>
              </article>

              <div className="card">
                <h4>LOGROS INDIVIDUALES POR PARTIDO</h4>
                <p>En cada encuentro, 6 jugadores son reconocidos en diferentes categorías:</p>
                <div className="card-content">
                  <div className="card-small"><i className="fa-solid fa-futbol icon"></i><b>El Jugón</b><span>Mayor influencia en la construcción del juego</span></div>
                  <div className="card-small"><i className="fa-solid fa-shield-halved icon"></i><b>El Cerrojo</b><span>Más sólido defensivamente</span></div>
                  <div className="card-small"><i className="fa-solid fa-bolt icon"></i><b>El Desequilibrante</b><span>Más determinante en ataque</span></div>
                </div>
                <div className="card-content">
                  <div className="card-small"><i className="fa-solid fa-crown icon"></i><b>El Líder</b><span>Mejor mentalidad ganadora y espíritu de equipo</span></div>
                  <div className="card-small"><i className="fa-solid fa-heart icon"></i><b>El Mejor Compañero</b><span>Valores y actitud</span></div>
                  <div className="card-small"><i className="fa-solid fa-people-group icon"></i><b>Jugador de Equipo</b><span>Antepone el beneficio del equipo al propio</span></div>
                </div>
                <h4>RECONOCIMIENTOS ESPECIALES CADA 4 JORNADAS</h4>
                <p>Algunos premios destacan el esfuerzo a medio plazo:</p>
                <div className="card-content">
                  <div className="card-small"><i className="fa-solid fa-arrow-up-right-dots icon"></i><b>Mayor Progresión</b><span>Jugador que más ha evolucionado en nivel y toma de decisiones</span></div>
                  <div className="card-small"><i className="fa-solid fa-calendar-check icon"></i><b>Premio a la Constancia</b><span>Jugador que asiste 4 jornadas seguidas</span></div>
                  <div className="card-small"><i className="fa-solid fa-medal icon"></i><b>Ganador Incansable</b><span>Jugador que consigue 4 victorias consecutivas</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* QUÉ OFRECE */}
        <section id="sistema">
          <h2>¿QUÉ OFRECE NUESTRO SISTEMA?</h2>
          <div className="row">
            <div className="col">
              <div className="article-card">
                <h3><i className="fa-solid fa-bullhorn icon"></i> RECONOCIMIENTO PÚBLICO</h3>
                <article><p>Cada semana, los jugadores que consiguen logros serán <b>reconocidos públicamente</b> en nuestras redes sociales. ¡Más de <b>60 menciones semanales</b>!</p></article>
              </div>
              <div className="article-card">
                <h3><i className="fa-solid fa-id-card icon"></i> CONSULTAR FICHA</h3>
                <article><p>Cada jugador tiene acceso a su <b>ficha personalizada</b>, donde podrá consultar los <b>ProPoints™</b> acumulados, los <b>logros conseguidos</b> y los <b>premios disponibles</b>.</p></article>
              </div>
              <div className="article-card">
                <h3><i className="fa-solid fa-lock icon"></i> PUNTUACIÓN PRIVADA</h3>
                <article><p>Nuestro sistema es <b>individualizado</b>, no hay ranking público ni clasificaciones. Aquí el foco está en la <b>mejora personal</b> y el <b>reconocimiento justo</b>.</p></article>
              </div>
            </div>
          </div>
        </section>

        {/* CATÁLOGO DE PREMIOS */}
        <section id="premios">
          <h2>CATÁLOGO DE PREMIOS PROPOINTS™</h2>
          <div className="row">
            <div className="col">
              <div className="card nivel-bronce">
                <h4>NIVEL BRONCE: 30 PUNTOS</h4>
                <p>Premios accesibles para jugadores constantes y comprometidos</p>
                <div className="card-content">
                  <div className="card-small"><i className="fa-solid fa-hourglass-half icon"></i><b>Premios por definir</b><span>De momento</span></div>
                </div>
              </div>

              <div className="card nivel-plata">
                <h4>NIVEL PLATA: 60 PUNTOS</h4>
                <p>Recompensas exclusivas para quienes van un paso más allá</p>
                <div className="card-content">
                  <div className="card-small"><i className="fa-solid fa-hourglass-half icon"></i><b>Premios por definir</b><span>De momento</span></div>
                </div>
              </div>

              <div className="card nivel-oro">
                <h4>NIVEL ORO: 90 PUNTOS</h4>
                <p>Para los jugadores más constantes, disciplinados y ejemplares</p>
                <div className="card-content">
                  <div className="card-small"><i className="fa-solid fa-hourglass-half icon"></i><b>Premios por definir</b><span>De momento</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* GANADORES POR CATEGORÍA */}
        <section id="ganadores">
          <h2>GANADORES POR CATEGORÍA</h2>
          <div className="row">
            <div className="col">
              <article>
                <p>Al finalizar la temporada, el jugador que haya ganado en cada categoría será llamado por la organización para felicitarle, y podrá elegir entre <b>experiencia</b> y <b>regalo ProKids Élite</b>.</p>
              </article>

              <div className="article-card">
                <h3><i className="fa-solid fa-wand-magic-sparkles icon"></i> EXPERIENCIA PROKIDS ÉLITE</h3>
                <article>
                  <ul>
                    <li><i className="fa-solid fa-video"></i> <b>ENTRENAMIENTO ESPECIAL CON:</b></li>
                    <li><i className="fa-solid fa-camera"></i> Grabación y fotografía</li>
                    <li><i className="fa-solid fa-chart-bar"></i> Análisis posterior básico</li>
                    <li><i className="fa-solid fa-film"></i> Vídeo individual tipo highlights</li>
                  </ul>
                </article>
              </div>

              <div className="article-card">
                <h3><i className="fa-solid fa-gift icon"></i> REGALOS PROKIDS ÉLITE</h3>
                <article>
                  <ul>
                    <li><i className="fa-solid fa-shirt"></i> EQUIPACIÓN EXCLUSIVA &ldquo;WINNER LIGA PROKIDS&rdquo;</li>
                    <li><i className="fa-solid fa-trophy"></i> TROFEO GRANDE ESPECIAL &ldquo;MVP LIGA PROKIDS&rdquo;</li>
                    <li><i className="fa-solid fa-camera-retro"></i> REPORTAJE DE FOTOS</li>
                  </ul>
                </article>
              </div>

              <div className="card">
                <h4>¡EMPIEZA A ACUMULAR PUNTOS HOY MISMO!</h4>
                <div className="card-content">
                  <p><b>Únete a La Liga ProKids y descubre todo lo que puedes conseguir con tu esfuerzo, dedicación y actitud positiva.</b></p>
                  <Link to={getRegisterUrl('liga')} className="button" style={{ width: '100%' }}>
                    <span>¡INSCRÍBETE AHORA!</span>
                    <br />
                    <span>Regístrate <i className="fa-solid fa-arrow-right"></i> Crea futbolista <i className="fa-solid fa-arrow-right"></i> Compra jornadas</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>
    </div>
  );
};

export default ProPoints;
